import { defineStore } from 'pinia'

export const useFleetStore = defineStore('fleet', {
  state: () => ({
    fleet: [],
    installations: [],
    usvs: [],
    agreements: [],
    deliveries: [],
    logbook: {
      ecdis: [],
      web: [],
    },
    transactions: [],
    folio: [],
  folioByDevice: {},
    weather: {},
    alerts: {},
    data: {
      navarea: [], // список предупреждений Navarea
      aio: {
        // список предупреждений AIO (Admiralty Information Overlay)
        tempInfos: [], // временные предупреждения AIO (Admiralty Information Overlay)
        permanentInfos: [], // постоянные предупреждения AIO (Admiralty Information Overlay)
      },
      ports: [], // порты
    },
    AISData: {},
    selectedShipPosition: null,
  }),
  getters: {
    getFleet: state => {
      let formattedFleet = []

      if (state.installations.length) {
        formattedFleet = state.installations.map(item => {
          const template = {
            ship: {
              status: 'unknown',
            },
          }

          template.ship.email = item.email
          template.ship.deviceId = item.deviceId
          template.ship.applicationId = item.applicationId

          const primarInfo = item?.primarInfo || null
          const shipInfo = primarInfo?.shipInfo || item?.appState?.shipInfo || {}
          const backupPermits = Array.isArray(primarInfo?.backupPermits) ? primarInfo.backupPermits : []

          template.ship.name = shipInfo?.name || ''
          template.ship.emails = primarInfo?.notificationEmails ?? item?.appState?.programInfo?.activeEmail ?? ''
          template.ship.imo = shipInfo?.imo || ''
          template.ship.mmsi = shipInfo?.mmsi || ''
          template.ship.call = shipInfo?.callSign || ''
          template.ship.flag = shipInfo?.flag || ''
          template.ship.category = shipInfo?.category || ''
          template.ship.ecdisBrand = shipInfo?.ecdisBrand || ''
          template.ship.ecdisOs = shipInfo?.ecdisOs || ''
          template.ship.ecdisType = shipInfo?.ecdisType || ''
          template.ship.length = shipInfo?.length ?? ''
          template.ship.width = shipInfo?.width ?? ''
          template.ship.height = shipInfo?.height ?? ''
          template.ship.draught = shipInfo?.draught ?? ''
          template.ship.upn1 = primarInfo?.primarPermit || ''
          template.ship.upn2 = backupPermits[0] || ''
          template.ship.upn3 = backupPermits[1] || ''
          template.ship.brand = shipInfo?.ecdisBrand ? shipInfo.ecdisBrand : 'unknown'
          template.ship.mssId = item?.mssId ?? ''

          // Find matching agreement for this device
          const matchingAgreement = state.agreements.find(agreement => agreement.users && agreement.users.some(user => user.deviceId === item.deviceId))

          if (matchingAgreement) {
            const user = matchingAgreement.users.find(u => u.deviceId === item.deviceId)
            template.ship.agreement = matchingAgreement.name
            template.ship.provider = user && user.availableOrderTypes?.length ? user.availableOrderTypes[0] : ''
          } else {
            template.ship.agreement = ''
            template.ship.provider = ''
          }

          return template
        })
      }

      // Add devices from agreements that aren't already in the list
      if (state.agreements.length) {
        const existingDeviceIds = new Set(formattedFleet.map(item => item.ship.deviceId))

        state.agreements.forEach(agreement => {
          if (!agreement.users) return

          agreement.users.forEach(user => {
            if (!existingDeviceIds.has(user.deviceId) && user.deviceId) {
              const template = {
                ship: {
                  status: 'unknown',
                },
              }

              // Fill data from agreement user
              template.ship.email = user.email || ''
              template.ship.deviceId = user.deviceId
              template.ship.applicationId = user.applicationId

              template.ship.agreement = agreement.name || ''
              template.ship.provider = user.availableOrderTypes?.length ? user.availableOrderTypes[0] : ''

              const userPrimarInfo = user?.primarInfo || null
              const userShipInfo = userPrimarInfo?.shipInfo || {}
              const userBackupPermits = Array.isArray(userPrimarInfo?.backupPermits) ? userPrimarInfo.backupPermits : []

              template.ship.name = userShipInfo?.name || ''
              template.ship.emails = userPrimarInfo?.notificationEmails ?? ''
              template.ship.imo = userShipInfo?.imo ?? ''
              template.ship.mmsi = userShipInfo?.mmsi ?? ''
              template.ship.call = userShipInfo?.callSign ?? ''
              template.ship.flag = userShipInfo?.flag ?? ''
              template.ship.category = userShipInfo?.category ?? ''
              template.ship.ecdisBrand = userShipInfo?.ecdisBrand ?? ''
              template.ship.ecdisOs = userShipInfo?.ecdisOs ?? ''
              template.ship.ecdisType = userShipInfo?.ecdisType ?? ''
              template.ship.length = userShipInfo?.length ?? ''
              template.ship.width = userShipInfo?.width ?? ''
              template.ship.height = userShipInfo?.height ?? ''
              template.ship.draught = userShipInfo?.draught ?? ''
              template.ship.upn1 = userPrimarInfo?.primarPermit || ''
              template.ship.upn2 = userBackupPermits[0] || ''
              template.ship.upn3 = userBackupPermits[1] || ''
              template.ship.brand = userShipInfo?.ecdisBrand ? userShipInfo.ecdisBrand : 'unknown'
              template.ship.mssId = user?.mssId ?? ''

              formattedFleet.push(template)
              existingDeviceIds.add(user.deviceId)
            }
          })
        })
      }

      // Remove any remaining entries with null or missing primarInfo:
      formattedFleet = formattedFleet.filter(item => item.ship && item.ship.name)

      // Sort the combined list alphabetically by ship name
      return formattedFleet.sort((a, b) => {
        const nameA = (a.ship.name || '').toLowerCase()
        const nameB = (b.ship.name || '').toLowerCase()
        return nameA.localeCompare(nameB)
      })
    },
    getUSVs: state => state.usvs || [],
    getLogbook: state => (state.logbook ? [...(state.logbook.web || []), ...(state.logbook.ecdis || [])] : []),
    getShipAISData(deviceId) {
      return this.AISData[deviceId] || null
    },
    hasNavarea: state => state.data.navarea.length > 0,
    hasAIO: state => state.data.aio.tempInfos.length > 0 || state.data.aio.permanentInfos.length > 0,
    hasPorts: state => state.data.ports.length > 0,
    hasInstallations: state => state.installations.length > 0,
    hasUSVs: state => state.usvs.length > 0,
    hasAgreements: state => state.agreements.length > 0,
    hasDeliveries: state => state.deliveries.length > 0,
    hasLogbook: state => state.logbook?.ecdis?.length > 0 || state.logbook?.web?.length > 0,
    hasOrders: state => state.transactions.length > 0,
    hasFolio: state => state.folio.length > 0,
    hasAISData: state => Object.keys(state.AISData).length > 0,
    hasFolioAlert: state => deviceId => {
      const entry = state.alerts?.folio?.[deviceId]
      if (!entry) return false

      if (typeof entry.hasAlert === 'boolean') {
        return entry.hasAlert
      }

      const status = entry.status
      return status === 'expired_all' || status === 'expiring'
    },
    getFolioCharts: state => deviceId => {
      const entry = state.alerts?.folio?.[deviceId]
      if (!entry) return []

      if (Array.isArray(entry.charts)) {
        return entry.charts.map(item => {
          if (typeof item === 'string') return item
          if (item && typeof item.id === 'string') return item.id
          if (item && typeof item.name === 'string') return item.name
          return ''
        }).filter(Boolean)
      }

      return []
    },
    getFolioStatus: state => deviceId => {
      return state.alerts?.folio?.[deviceId] || null
    },
    hasDeviceFolio: state => deviceId => {
      const orders = state.folioByDevice?.[deviceId]
      return Array.isArray(orders) && orders.length > 0
    },
    getDeviceFolio: state => deviceId => {
      return state.folioByDevice?.[deviceId] || []
    },
    // Проверяем, есть ли алерты по погоде для конкретного deviceId
    hasWeatherAlerts: state => deviceId => {
      const alertsForDevice = state.alerts[deviceId] || []
      return alertsForDevice.some(alert => alert.type === 'Wind' && (alert.color === 'Yellow' || alert.color === 'Red'))
    },
    // Проверяем, есть ли алерты по Navarea для конкретного deviceId
    hasNavareaAlerts: state => deviceId => {
      const alertsForDevice = state.alerts[deviceId] || []
      return alertsForDevice.some(alert => alert.type === 'NavArea' && (alert.color === 'Yellow' || alert.color === 'Red'))
    },
    // Проверяем есть ли любые алерты для конкретного deviceId
    hasAnyAlerts: state => deviceId => {
      return state.hasWeatherAlerts(deviceId) || state.hasNavareaAlerts(deviceId)
    },
    // Получаем имя корабля по deviceId из installations
    getShipName: state => deviceId => {
      const installation = state.installations.find(item => item.deviceId === deviceId)
      return installation?.primarInfo?.shipInfo?.name || ''
    },
  },
  actions: {
    setSelectedShipPosition(position) {
      this.selectedShipPosition = position
    },
    hasAnyAlertsForShip(deviceId) {
      // Check if any alerts exist for this device
      return (
        // Weather alert
        (this.weather && this.weather[deviceId] && this.weather[deviceId].alert) ||
        // Folio alert
        this.hasFolioAlert(deviceId) ||
        // Navarea warning
        (this.alerts && this.alerts.navarea && this.alerts.navarea[deviceId]) ||
        // Logbook alert
        (this.logbook &&
          (this.logbook.ecdis?.some(entry => entry.deviceId === deviceId && entry.type === 'error') ||
            this.logbook.web?.some(entry => entry.deviceId === deviceId && entry.type === 'error')))
      )
    },
    addFleet(data) {
      this.fleet = data
    },
    setFolioAlerts(deviceId, data) {
      if (!this.alerts) this.alerts = {}
      if (!this.alerts.folio) this.alerts.folio = {}

      const existing = this.alerts.folio[deviceId] || {}
      this.alerts.folio[deviceId] = {
        ...existing,
        ...data,
      }
    },
    addAISDataFromFile(data) {
      this.AISData = data
    },
    addWeatherDataFromFile(data) {
      this.weather = data
    },
    addAISData(deviceId, data) {
      this.AISData = {
        ...this.AISData,
        [deviceId]: data,
      }
    },
    addWeatherData(deviceId, data) {
      this.weather = {
        ...this.weather,
        [deviceId]: data,
      }
    },
    addAlertsData(deviceId, data) {
      this.alerts = {
        ...this.alerts,
        [deviceId]: data,
      }
    },
    addInstallations(data) {
      this.installations = data
    },
    addUSVs(data) {
      this.usvs = data;
    },
    addAgreements(data) {
      this.agreements = data
    },
    addDeliveries(data) {
      this.deliveries = data
    },
    addLogbook_ecdis(data) {
      this.logbook.ecdis = data
    },
    addLogbook_web(data) {
      this.logbook.web = data
    },
    addFolio(deviceIdOrData, dataMaybe, options = {}) {
      const isLegacyCall = Array.isArray(deviceIdOrData) && dataMaybe === undefined

      if (isLegacyCall) {
        this.folio = deviceIdOrData
        return
      }

      const deviceId = deviceIdOrData
      const orders = Array.isArray(dataMaybe) ? dataMaybe : []
      const { silent = false } = options || {}

      this.folioByDevice = {
        ...this.folioByDevice,
        [deviceId]: orders,
      }

      if (!silent) {
        this.folio = orders
      }
    },
    addOrders(data) {
      this.transactions = data
    },
    addAIO(tempData, permanentData) {
      this.data.aio.tempInfos = Object.freeze(tempData)
      this.data.aio.permanentInfos = Object.freeze(permanentData)
    },
    addNavarea(data) {
      this.data.navarea = Object.freeze(data)
    },
    addPorts(data) {
      this.data.ports = Object.freeze(data)
    },
    reset() {
      this.fleet = []
      this.installations = []
      this.transactions = []
      this.deliveries = []
      this.logbook.ecdis = []
      this.logbook.web = []
      this.weather = {}
      this.alerts.navarea = [] // TODO
      this.alerts = {}
      this.AISData = {}
      this.folio = []
  this.folioByDevice = {}
      this.data.navarea = []
      this.data.aio = []
    },

    // Принудительная очистка больших массивов данных для освобождения памяти
    forceCleanupMemory() {
      try {
        // Очищаем большие массивы с созданием новых массивов вместо изменения length
        if (this.data.aio.tempInfos) {
          this.data.aio.tempInfos = []
        }

        if (this.data.aio.permanentInfos) {
          this.data.aio.permanentInfos = []
        }

        if (this.data.navarea) {
          this.data.navarea = []
        }

        if (this.data.ports) {
          this.data.ports = []
        }

        // Очищаем объекты с большим количеством данных
        if (this.AISData) {
          Object.keys(this.AISData).forEach(key => {
            delete this.AISData[key]
          })
          this.AISData = {}
        }

        if (this.weather) {
          Object.keys(this.weather).forEach(key => {
            delete this.weather[key]
          })
          this.weather = {}
        }

        // Очищаем другие массивы
        this.fleet = []
        this.installations = []
        this.transactions = []
        this.deliveries = []
        this.logbook.ecdis = []
        this.logbook.web = []
        this.folio = []
        this.alerts = {}

        // Принудительная сборка мусора если доступна
        if (typeof window !== 'undefined' && window.gc && typeof window.gc === 'function') {
          try {
            window.gc()
          } catch (e) {
            // Игнорируем ошибки GC
          }
        }
      } catch (error) {
        console.warn('Ошибка при принудительной очистке памяти:', error)
      }
    },
  },
  persist: false,
})
